import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useWorkspace } from '@/features/workspace-sub-delegation';
import {
  shareContactCardAsAdvisor,
  shareContactCardAsProvider,
  type ShareContactCardRequest,
  type ShareContactCardResponse,
} from '../api/contactCardShareApi';

/**
 * Sends a provider's contact card off-platform, via the route that matches the
 * sender's role.
 *
 * The two backend routes are guarded separately — the advisor one by role, the
 * provider one by workspace scoping — so posting to the wrong one is a 403
 * rather than a soft failure. Dispatching here keeps every calling surface from
 * having to know that, and there are five of them.
 *
 * `effectiveRole` rather than the literal role: a sub-delegate acts under the
 * workspace owner's role, matching how the referral flows already behave.
 *
 * No cache invalidation: nothing about the send is stored, so there is no list
 * or count anywhere that could go stale.
 */
export function useShareContactCard(options?: {
  onSuccess?: (data: ShareContactCardResponse) => void;
  onError?: (error: Error) => void;
}) {
  const { user } = useAuth();
  const { effectiveRole, activeWorkspace } = useWorkspace();
  const role = effectiveRole ?? user?.role;
  // The SP route takes the *sending* SP's user id in the path, and its guard
  // rejects any id that is not the workspace owner. For everyone but a delegate
  // the two are the same id.
  const senderUserId = activeWorkspace?.ownerId ?? user?.id;

  return useMutation<ShareContactCardResponse, Error, ShareContactCardRequest>({
    mutationFn: (data: ShareContactCardRequest) => {
      if (role === 'FINANCIAL_ADVISOR') return shareContactCardAsAdvisor(data);

      if (role === 'SERVICE_PROVIDER') {
        if (!senderUserId) throw new Error('User ID is required');
        return shareContactCardAsProvider(senderUserId, data);
      }

      // Neither pro role: the backend would reject this anyway, and failing here
      // says why instead of surfacing a bare 403 to someone who should never
      // have seen the button.
      throw new Error('Only a Financial Advisor or Service Provider can share a contact card.');
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}
